import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "atelier_cart";

const readCart = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export function CartProvider({ children }) {
  // Lazy init so we hydrate synchronously from localStorage BEFORE any save effect runs
  const [items, setItems] = useState(readCart);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const keyOf = (i) => `${i.product_id}::${i.size || ""}::${i.color || ""}`;

  const addItem = (product, { size, color, quantity = 1 }) => {
    const newItem = {
      product_id: product.id,
      name: product.name,
      image: (product.images || [])[0] || "",
      price: Number(product.price) * (1 - Number(product.discount_percent || 0) / 100),
      size: size || null,
      color: color || null,
      quantity,
    };
    setItems((prev) => {
      const idx = prev.findIndex((p) => keyOf(p) === keyOf(newItem));
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [...prev, newItem];
    });
    setDrawerOpen(true);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((p) => keyOf(p) !== key));

  const updateQty = (key, qty) => {
    if (qty <= 0) return removeItem(key);
    setItems((prev) => prev.map((p) => (keyOf(p) === key ? { ...p, quantity: qty } : p)));
  };

  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + Number(i.price) * i.quantity, 0),
    [items]
  );

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQty,
        clear,
        subtotal,
        count,
        keyOf,
        drawerOpen,
        setDrawerOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
